import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'
import { BellOff, ClipboardList, FileText, ListChecks, Loader2, Mail, Package } from 'lucide-react'
import { PageHeader } from '@/components/layout/page-header'
import { badgeVariants } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { StatCard } from '@/components/ui/stat'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { apiClient } from '@/lib/api/client'
import type { HelmAttentionItem, HelmAttentionState, HelmUser } from '@/lib/api/types'
import { cn } from '@/lib/utils'
import { ErrorState } from '@/src/components/ErrorState'
import { LoadingState } from '@/src/components/LoadingState'
import { useAsyncData } from '@/src/hooks/useAsyncData'
import { getHelmSessionUser } from '@/src/lib/auth'

const REFRESH_MS = 60_000

type AttentionView = 'all' | 'mine' | 'triage'

const VIEWS: Array<{ value: AttentionView; label: string; owner?: 'me' | 'unassigned' }> = [
  { value: 'all', label: 'All' },
  { value: 'mine', label: 'My work', owner: 'me' },
  { value: 'triage', label: 'Unassigned', owner: 'unassigned' },
]

// Late and escalated is the one red; late is amber, like anything waiting.
const STATE: Record<HelmAttentionState, { label: string; tone: string }> = {
  ESCALATED: { label: 'Escalated', tone: 'tone-blocked' },
  OVERDUE: { label: 'Overdue', tone: 'tone-waiting' },
  DUE_SOON: { label: 'Due soon', tone: 'tone-neutral' },
  UPCOMING: { label: 'Coming up', tone: 'tone-closed' },
}

const KIND: Record<HelmAttentionItem['kind'], { icon: LucideIcon; label: string }> = {
  reply: { icon: Mail, label: 'Reply' },
  quote: { icon: FileText, label: 'Quotation' },
  inquiry: { icon: ClipboardList, label: 'Inquiry' },
  task: { icon: ListChecks, label: 'Task' },
  consignment: { icon: Package, label: 'Consignment' },
}

/** Business time is Kampala time, wherever the reader is. */
function kampala(iso: string) {
  return new Date(iso).toLocaleString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Africa/Kampala',
  })
}

function personName(user: HelmUser | undefined) {
  if (!user) return 'Someone'
  return user.display_name || user.name || user.email.split('@')[0]
}

function StateBadge({ state }: { state: HelmAttentionState }) {
  return (
    <span className={cn(badgeVariants({ variant: 'status' }), STATE[state].tone)}>
      {STATE[state].label}
    </span>
  )
}

interface ListProps {
  items: HelmAttentionItem[]
  empty: string
  meId: string | null
  users: Map<string, HelmUser>
  taking: string | null
  onTake: (item: HelmAttentionItem) => void
}

function Owner({
  item,
  meId,
  users,
  taking,
  onTake,
}: Omit<ListProps, 'items' | 'empty'> & {
  item: HelmAttentionItem
}) {
  if (item.responsible_user_id) {
    return item.responsible_user_id === meId
      ? 'You'
      : personName(users.get(item.responsible_user_id))
  }
  // An unowned inquiry can be taken from here; a task is assigned on its record.
  if (item.inquiry_id && !item.task_id && meId) {
    return (
      <Button
        variant="outline"
        size="xs"
        disabled={taking === item.key}
        onClick={() => onTake(item)}
      >
        {taking === item.key && <Loader2 className="animate-spin" />}
        Take it
      </Button>
    )
  }
  return <span className="text-muted-foreground">Unassigned</span>
}

function What({ item }: { item: HelmAttentionItem }) {
  const Kind = KIND[item.kind]
  return (
    <div className="flex items-start gap-2">
      <Kind.icon aria-label={Kind.label} className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <div className="font-medium">{item.title}</div>
        {item.detail && <div className="text-xs text-muted-foreground">{item.detail}</div>}
      </div>
    </div>
  )
}

function For({ item }: { item: HelmAttentionItem }) {
  return (
    <>
      <Link to={item.link_path} className="font-medium hover:underline">
        {item.subject}
      </Link>
      {item.customer_name && (
        <div className="text-xs text-muted-foreground">{item.customer_name}</div>
      )}
    </>
  )
}

function Due({ item }: { item: HelmAttentionItem }) {
  return (
    <>
      <div>{kampala(item.due_at)}</div>
      {item.overdue_label && (
        <div
          className={cn('text-xs', item.state === 'ESCALATED' ? 'text-blocked' : 'text-waiting')}
        >
          {item.overdue_label} late
        </div>
      )}
    </>
  )
}

/** A table on a wide screen; on a phone, one card per item so nothing scrolls sideways. */
function AttentionList({ items, empty, ...owner }: ListProps) {
  if (items.length === 0) {
    return (
      <Card>
        <p className="px-6 text-sm text-muted-foreground">{empty}</p>
      </Card>
    )
  }
  return (
    <>
      <Card className="hidden md:flex">
        {/* Fixed columns, so the two lists on the page line up. */}
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="w-32">State</TableHead>
              <TableHead>What</TableHead>
              <TableHead className="w-[30%]">For</TableHead>
              <TableHead className="w-32">Owner</TableHead>
              <TableHead className="w-48">Due</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.key}>
                <TableCell className="align-top">
                  <StateBadge state={item.state} />
                </TableCell>
                <TableCell className="align-top whitespace-normal">
                  <What item={item} />
                </TableCell>
                <TableCell className="align-top whitespace-normal">
                  <For item={item} />
                </TableCell>
                <TableCell className="align-top">
                  <Owner item={item} {...owner} />
                </TableCell>
                <TableCell className="align-top">
                  <Due item={item} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <ul className="divide-y rounded-2xl border bg-card md:hidden">
        {items.map((item) => (
          <li key={item.key} className="space-y-2 px-4 py-3 text-sm">
            <div className="flex items-start justify-between gap-3">
              <StateBadge state={item.state} />
              <div className="text-right">
                <Due item={item} />
              </div>
            </div>
            <What item={item} />
            <div>
              <For item={item} />
            </div>
            <div className="flex items-center justify-between gap-3 text-muted-foreground">
              <span>Owner</span>
              <span className="text-foreground">
                <Owner item={item} {...owner} />
              </span>
            </div>
          </li>
        ))}
      </ul>
    </>
  )
}

/**
 * What the team owes clients: unanswered messages, quotations not yet sent,
 * inquiries, dated tasks and consignments due, most urgent first, in Kampala
 * working hours. The same list the follow-up alerts come from.
 */
export function AttentionPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const requested = searchParams.get('view')
  const view: AttentionView = requested === 'mine' || requested === 'triage' ? requested : 'all'
  const owner = VIEWS.find((entry) => entry.value === view)!.owner
  const [taking, setTaking] = useState<string | null>(null)
  const { data, loading, error, reload } = useAsyncData(
    () => apiClient.getAttention(owner),
    [owner],
  )
  const people = useAsyncData(async () => {
    const [users, me] = await Promise.all([
      apiClient.listHelmUsers().catch(() => ({ users: [] as HelmUser[] })),
      getHelmSessionUser().catch(() => null),
    ])
    return { users: new Map(users.users.map((user) => [user.id, user])), meId: me?.id ?? null }
  }, [])

  // Overdue is a matter of time passing, so the list refreshes itself. The
  // hook hands out a new `reload` each render; the timer keeps the latest.
  const reloadRef = useRef(reload)
  useEffect(() => {
    reloadRef.current = reload
  })
  useEffect(() => {
    const refresh = () => reloadRef.current()
    const timer = window.setInterval(refresh, REFRESH_MS)
    window.addEventListener('focus', refresh)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('focus', refresh)
    }
  }, [])

  function changeView(next: string) {
    setSearchParams(next === 'all' ? {} : { view: next }, { replace: true })
  }

  async function takeIt(item: HelmAttentionItem) {
    const meId = people.data?.meId
    if (!meId || !item.inquiry_id) return
    setTaking(item.key)
    try {
      await apiClient.updateInquiry(item.inquiry_id, { owner_user_id: meId })
      reload()
    } finally {
      setTaking(null)
    }
  }

  if (loading && !data) return <LoadingState label="Loading what needs attention..." />
  if (error && !data) return <ErrorState error={error} onRetry={reload} />
  const items = data?.items ?? []
  const now = items.filter((item) => item.state !== 'UPCOMING')
  const later = items.filter((item) => item.state === 'UPCOMING')
  const counts = data?.counts
  const table = {
    meId: people.data?.meId ?? null,
    users: people.data?.users ?? new Map<string, HelmUser>(),
    taking,
    onTake: (item: HelmAttentionItem) => void takeIt(item),
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Attention"
        description="What the team owes clients: replies, quotations, inquiries, tasks and consignments due, timed in Kampala working hours."
      />

      {data && !data.alerts_enabled && (
        <p className="flex items-start gap-2 text-sm text-muted-foreground">
          <BellOff aria-hidden className="mt-0.5 size-4 shrink-0" />
          Helm lists these here but does not alert anyone about them yet. Alerts start once
          follow-through alerts are switched on.
        </p>
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
        <StatCard label="Escalated" value={counts?.escalated ?? '—'} />
        <StatCard label="Overdue" value={counts?.overdue ?? '—'} />
        <StatCard label="Due soon" value={counts?.due_soon ?? '—'} />
        <StatCard label="Coming up" value={counts?.upcoming ?? '—'} />
      </div>

      <Tabs value={view} onValueChange={changeView}>
        <TabsList aria-label="Whose work">
          {VIEWS.map((entry) => (
            <TabsTrigger key={entry.value} value={entry.value}>
              {entry.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <section className="space-y-3" aria-labelledby="attention-now">
        <h2 id="attention-now" className="text-lg font-semibold">
          Needs attention
        </h2>
        <AttentionList
          items={now}
          empty="Nothing is late or due in the next two working hours."
          {...table}
        />
      </section>

      <section className="space-y-3" aria-labelledby="attention-later">
        <h2 id="attention-later" className="text-lg font-semibold">
          Coming up
        </h2>
        <AttentionList items={later} empty="Nothing else has a deadline yet." {...table} />
      </section>
    </div>
  )
}
